"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateViewSchema = exports.saveViewSchema = exports.savedViewStateSchema = exports.PIPELINE_LAYOUTS = exports.transferApplicationSchema = exports.pipelineExportSchema = exports.bulkActionSchema = exports.BULK_ACTION_LABELS = exports.BULK_ACTIONS = exports.pipelineSelectionSchema = exports.moveApplicationSchema = exports.PIPELINE_HIDDEN_BY_DEFAULT = exports.PIPELINE_COLUMN_LABELS = exports.PIPELINE_COLUMNS = exports.pipelineListQuerySchema = exports.PIPELINE_FILTER_KEYS = exports.pipelineFilterSchema = exports.DEFAULT_PIPELINE_SORT = exports.PIPELINE_SORT_COLUMNS = exports.PIPELINE_FLAG_LABELS = exports.PIPELINE_FLAGS = exports.NO_SCREENING_GUARD = exports.DEBRIEF_ONLY = exports.HOLD_RELEASE_ONLY = exports.COMPARISON_ONLY = exports.STAGE_GROUPS = exports.CLOSED_PIPELINE_STAGES = exports.BOARD_STAGES = exports.BULK_TOO_MANY = exports.TRANSFER_NOTE_MAX = exports.SAVED_VIEW_NAME_MAX = exports.TAG_NAME_MAX = exports.PIPELINE_SCREEN_KEY = exports.BOARD_COLUMN_CARD_LIMIT = exports.PIPELINE_PAGE_SIZE = exports.BULK_UNDO_MINUTES = exports.BULK_SYNC_MAX = exports.BULK_MAX_RECORDS = void 0;
exports.isBoardStage = isBoardStage;
exports.nextStageOf = nextStageOf;
exports.hasClearedScreening = hasClearedScreening;
exports.checkMove = checkMove;
exports.acceptsRejection = acceptsRejection;
exports.parsePipelineFilter = parsePipelineFilter;
exports.stagesOf = stagesOf;
exports.sortOf = sortOf;
exports.sortToString = sortToString;
exports.activeFilterCount = activeFilterCount;
exports.reasonsForStages = reasonsForStages;
exports.bulkResultMessage = bulkResultMessage;
const communication_1 = require("./communication");
const zod_1 = require("zod");
const decisions_1 = require("./decisions");
const jobs_1 = require("./jobs");
const reason_codes_1 = require("./reason-codes");
const schemas_1 = require("./schemas");
const state_machines_1 = require("./state-machines");
// S16 — pipeline views (SCR-02 list, SCR-03 board) and bulk actions. Sources: master
// plan S16 and its planning decisions (DECISIONS-LOG S16 Q1–Q7), F8.11, F8.13–F8.16,
// §L9, §L22 N-73, §L23, Appendix A, 06-qol §3–§5.
// --- §L9 limits ------------------------------------------------------------------------
exports.BULK_MAX_RECORDS = 200;
/** "≤ 25 records execute immediately"; above runs in the background with progress. */
exports.BULK_SYNC_MAX = 25;
/** "10 minutes for reversible actions". */
exports.BULK_UNDO_MINUTES = 10;
exports.PIPELINE_PAGE_SIZE = 100;
/** A board column shows its best cards; the header still counts all of them. */
exports.BOARD_COLUMN_CARD_LIMIT = 100;
exports.PIPELINE_SCREEN_KEY = 'pipeline';
exports.TAG_NAME_MAX = 40;
exports.SAVED_VIEW_NAME_MAX = 60;
exports.TRANSFER_NOTE_MAX = 1000;
exports.BULK_TOO_MANY = `Select ${exports.BULK_MAX_RECORDS} or fewer at a time.`;
// --- Stages ----------------------------------------------------------------------------
/** The board's columns, in the one order Appendix A allows (ADR 0013). `hold` sits beside the interviews. */
exports.BOARD_STAGES = [
    'new',
    'screening',
    'assessment',
    'interview_1',
    'interview_2',
    'interview_3',
    'hold',
    'offer',
    'hired_pending_join',
];
/** Where an application has left the pipeline. The board counts them; the list shows them on request. */
exports.CLOSED_PIPELINE_STAGES = [
    'rejected',
    'auto_rejected',
    'withdrawn',
    'transferred',
    'closed_job',
    'joined',
];
exports.STAGE_GROUPS = ['active', 'closed', 'all'];
function isBoardStage(stage) {
    return exports.BOARD_STAGES.includes(stage);
}
/** S22: the offer is the final selection's, made on the job's comparison (DEC-02). */
exports.COMPARISON_ONLY = "Choose who is selected on the job's Compare tab, or from the test results when the job has no interview round.";
/** S22: a hold leaves for an offer or a pool, released on the comparison (§L18, F12.11). */
exports.HOLD_RELEASE_ONLY = "Release a hold on the job's Compare tab — to an offer, or to a pool.";
/** Stages nothing may be moved INTO from the board: their own screens put people there. */
const NOT_YET_REACHABLE = {
    offer: exports.COMPARISON_ONLY,
    hired_pending_join: 'Someone is hired when they accept an offer, which arrives with the offer screens.',
    joined: 'Joining is confirmed when the hire is converted, which arrives with hiring.',
};
const INTERVIEW_STAGES = ['interview_1', 'interview_2', 'interview_3'];
/** S21 Q4: the words the board and the server give when a move belongs to the round's debrief. */
exports.DEBRIEF_ONLY = "Decide this on the round's debrief, where the panel's marks and the round's result are.";
/**
 * F8.03 "moves to next stage": the next stage the job has switched on, along the fixed
 * route. Null at the end of the route, and for `hold` (it leaves only for an offer or a
 * rejection, §L18).
 */
function nextStageOf(from, activeStages) {
    const path = jobs_1.PIPELINE_PATH_STAGES;
    const index = path.indexOf(from);
    if (index < 0)
        return null;
    return path.slice(index + 1).find((stage) => activeStages.has(stage)) ?? null;
}
exports.NO_SCREENING_GUARD = { isOn: false, managerResponse: null };
function hasClearedScreening(managerResponse) {
    return managerResponse === 'yes' || managerResponse === 'hr_proceeded';
}
const SCREENING_NOT_CLEARED = "This hasn't been through Review, Shortlist and Contact yet. Only HR can move it on early.";
/**
 * F8.13 "same validation as button actions": one answer to "may this role move this
 * application from here to there", used by the board to shade a column while a card is
 * dragged and by the server, which is the one that decides. Rejection's reason and a
 * skip's note are checked by the caller — this says whether they are needed.
 */
function checkMove(from, to, activeStages, role, screeningGuard = exports.NO_SCREENING_GUARD) {
    if (from === to)
        return refused('It is already there.');
    if (state_machines_1.APPLICATION_MACHINE.terminal.includes(from))
        return refused('This application is closed, so it no longer moves.');
    if (to === 'rejected') {
        return (0, state_machines_1.canTransition)(state_machines_1.APPLICATION_MACHINE, from, 'rejected', role)
            ? { isAllowed: true, isSkip: false, needsReason: true }
            : refused("You can't reject applications from this stage.");
    }
    if (to === 'withdrawn')
        return refused('A withdrawal is recorded from the application, with the reason the candidate gave.');
    if (to === 'transferred')
        return refused('Use "Transfer to another job" — it keeps this application and starts a new one there.');
    if (to === 'closed_job' || to === 'auto_rejected')
        return refused("That happens on its own; it can't be chosen.");
    const later = NOT_YET_REACHABLE[to];
    if (later)
        return refused(later);
    if (to === 'hold') {
        if (!(0, state_machines_1.canTransition)(state_machines_1.APPLICATION_MACHINE, from, 'hold', role))
            return refused('Only someone past an interview can be put on hold.');
        // Appendix A as clarified in S21: a hold is a round's decision, recorded on its debrief.
        return refused(exports.DEBRIEF_ONLY);
    }
    if (from === 'hold')
        return refused(exports.HOLD_RELEASE_ONLY);
    if (!activeStages.has(to))
        return refused(`This job doesn't use ${jobs_1.PIPELINE_STAGE_LABELS[to]}.`);
    const path = jobs_1.PIPELINE_PATH_STAGES;
    if (path.indexOf(to) < path.indexOf(from)) {
        return refused(`Applications only move forward. Undo a move within ${exports.BULK_UNDO_MINUTES} minutes instead.`);
    }
    const next = nextStageOf(from, activeStages);
    if (to !== next) {
        // §L23: "Skip a pipeline stage — HRL only, reason mandatory".
        return state_machines_1.SKIP_STAGE_ROLES.includes(role)
            ? { isAllowed: true, isSkip: true, needsReason: true }
            : refused('Only HR can skip a stage.');
    }
    // Appendix A: "interview_N → interview_N+1 — requires a StageDecision", recorded on the debrief (S21).
    if (INTERVIEW_STAGES.includes(from) && INTERVIEW_STAGES.includes(to))
        return refused(exports.DEBRIEF_ONLY);
    // D91: leaving Screening before Review, Shortlist and Contact finished is a skip too, while the guard is on.
    if (from === 'screening' && screeningGuard.isOn && !hasClearedScreening(screeningGuard.managerResponse)) {
        return state_machines_1.SKIP_STAGE_ROLES.includes(role)
            ? { isAllowed: true, isSkip: true, needsReason: true, isScreeningGuard: true }
            : refused(SCREENING_NOT_CLEARED);
    }
    return (0, state_machines_1.canTransition)(state_machines_1.APPLICATION_MACHINE, from, to, role)
        ? { isAllowed: true, isSkip: false, needsReason: false }
        : refused("You can't move applications to this stage.");
}
/** A rejection reason must fit where the application is (§L19) and the move must accept it. */
function acceptsRejection(from, reasonCode) {
    return (0, state_machines_1.acceptsReason)(state_machines_1.APPLICATION_MACHINE, from, 'rejected', reasonCode);
}
function refused(reason) {
    return { isAllowed: false, reason };
}
// --- Filters (URL and API alike, 06-qol §6) ---------------------------------------------
exports.PIPELINE_FLAGS = ['reapplicant', 'referred', 'elsewhere', 'pooled', 'missing_must_have', 'knockout'];
exports.PIPELINE_FLAG_LABELS = {
    reapplicant: 'Applied before',
    referred: 'Referred',
    elsewhere: 'Active on another job',
    pooled: 'In a talent pool',
    missing_must_have: 'Missing a must-have',
    knockout: 'Flagged by a screening question',
};
exports.PIPELINE_SORT_COLUMNS = ['candidate', 'stage', 'score', 'days', 'applied', 'source', 'recruiter'];
exports.DEFAULT_PIPELINE_SORT = [{ column: 'score', direction: 'desc' }];
/**
 * Every filter as the address bar holds it: strings only, so a copied link is the view
 * (S16 QA test 5). The same object travels inside an "all matching" bulk request, so
 * the server acts on exactly what the screen showed.
 */
exports.pipelineFilterSchema = zod_1.z.object({
    /** A stage group, or stages separated by commas. Absent is `active`. */
    stage: zod_1.z.string().max(300).optional(),
    q: zod_1.z.string().trim().max(100).optional(),
    scoreMin: zod_1.z.string().regex(/^\d{1,3}$/).optional(),
    scoreMax: zod_1.z.string().regex(/^\d{1,3}$/).optional(),
    source: zod_1.z.string().max(300).optional(),
    /** A user id, or `none` for unassigned. */
    recruiter: zod_1.z.string().max(40).optional(),
    tag: zod_1.z.string().max(400).optional(),
    flag: zod_1.z.string().max(200).optional(),
    appliedFrom: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    appliedTo: zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /** `score:desc,applied:asc` — shift-click adds a second column (06-qol §3). */
    sort: zod_1.z.string().max(200).optional(),
});
exports.PIPELINE_FILTER_KEYS = [
    'stage',
    'q',
    'scoreMin',
    'scoreMax',
    'source',
    'recruiter',
    'tag',
    'flag',
    'appliedFrom',
    'appliedTo',
    'sort',
];
exports.pipelineListQuerySchema = exports.pipelineFilterSchema.extend({
    page: zod_1.z.coerce.number().int().positive().default(1),
});
const MIN_QUERY = 2;
const SCORE_MAX = 100;
/** What the strings mean. Anything unrecognised is dropped rather than failing the page. */
function parsePipelineFilter(filter) {
    const score = (value) => (value === undefined ? null : Math.min(SCORE_MAX, Number(value)));
    const query = filter.q?.trim() ?? '';
    return {
        stages: stagesOf(filter.stage),
        query: query.length >= MIN_QUERY ? query : null,
        scoreMin: score(filter.scoreMin),
        scoreMax: score(filter.scoreMax),
        sources: listOf(filter.source),
        recruiter: filter.recruiter === 'none' || schemas_1.uuidSchema.safeParse(filter.recruiter).success ? (filter.recruiter ?? null) : null,
        tagIds: listOf(filter.tag).filter((id) => schemas_1.uuidSchema.safeParse(id).success),
        flags: listOf(filter.flag).filter((flag) => exports.PIPELINE_FLAGS.includes(flag)),
        appliedFrom: filter.appliedFrom ?? null,
        appliedTo: filter.appliedTo ?? null,
        sort: sortOf(filter.sort),
    };
}
function stagesOf(value) {
    if (value === undefined || value === '' || value === 'active')
        return exports.BOARD_STAGES;
    if (value === 'closed')
        return exports.CLOSED_PIPELINE_STAGES;
    if (value === 'all')
        return [...exports.BOARD_STAGES, ...exports.CLOSED_PIPELINE_STAGES];
    const all = [...exports.BOARD_STAGES, ...exports.CLOSED_PIPELINE_STAGES];
    const picked = listOf(value).filter((stage) => all.includes(stage));
    return picked.length > 0 ? picked : exports.BOARD_STAGES;
}
function sortOf(value) {
    const parsed = listOf(value).flatMap((part) => {
        const [column, direction] = part.split(':');
        if (column === undefined || !isSortColumn(column))
            return [];
        return [{ column, direction: direction === 'asc' ? 'asc' : 'desc' }];
    });
    return parsed.length > 0 ? parsed : exports.DEFAULT_PIPELINE_SORT;
}
function sortToString(sort) {
    return sort.map((spec) => `${spec.column}:${spec.direction}`).join(',');
}
function isSortColumn(value) {
    return exports.PIPELINE_SORT_COLUMNS.includes(value);
}
function listOf(value) {
    return value ? value.split(',').map((part) => part.trim()).filter(Boolean) : [];
}
/** How many filters the chips show — sort is not a filter. */
function activeFilterCount(filter) {
    return exports.PIPELINE_FILTER_KEYS.filter((key) => key !== 'sort' && filter[key] !== undefined && filter[key] !== '').length;
}
// --- Columns (DECISIONS-LOG S16 Q7) ----------------------------------------------------
exports.PIPELINE_COLUMNS = [
    'candidate',
    'stage',
    'score',
    'days',
    'applied',
    'source',
    'recruiter',
    'tags',
    'flags',
    'email',
    'mobile',
    'city',
    'experience',
    'education',
    'expectedSalary',
    'noticePeriod',
    'reference',
];
exports.PIPELINE_COLUMN_LABELS = {
    candidate: 'Candidate',
    stage: 'Stage',
    score: 'Score',
    days: 'Days in stage',
    applied: 'Applied',
    source: 'Source',
    recruiter: 'Recruiter',
    tags: 'Tags',
    flags: 'Flags',
    email: 'Email',
    mobile: 'Mobile',
    city: 'City',
    experience: 'Experience',
    education: 'Education',
    expectedSalary: 'Expected salary',
    noticePeriod: 'Notice period',
    reference: 'Reference',
};
/** Hidden until someone shows them (Q7). */
exports.PIPELINE_HIDDEN_BY_DEFAULT = [
    'email',
    'mobile',
    'city',
    'experience',
    'education',
    'expectedSalary',
    'noticePeriod',
    'reference',
];
// --- Requests ---------------------------------------------------------------------------
const noteSchema = zod_1.z.string().trim().max(exports.TRANSFER_NOTE_MAX, `Notes can be up to ${exports.TRANSFER_NOTE_MAX} characters.`);
const reasonCodeSchema = zod_1.z.string().trim().min(1, 'Pick a reason before continuing.').max(40);
const poolNameSchema = zod_1.z.string().trim().min(1, 'Give the pool a name.').max(80, 'Pool names can be up to 80 characters.');
const tagNameSchema = zod_1.z.string().trim().min(1, 'Give the tag a name.').max(exports.TAG_NAME_MAX, `Tags can be up to ${exports.TAG_NAME_MAX} characters.`);
/** F8.13 — one card dragged on the board, or moved from a row. */
exports.moveApplicationSchema = zod_1.z
    .object({
    to: zod_1.z.enum([...exports.BOARD_STAGES, 'rejected']),
    version: zod_1.z.number().int().nonnegative(),
    reasonCode: reasonCodeSchema.optional(),
    poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
    newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
    /** ADR 0046: "Also add to CV Bank" in the reject box; ignored for anything but a rejection. */
    addToCvBank: zod_1.z.boolean().default(false),
    /** F8.16: why a stage is skipped — mandatory for a skip, checked by the service. */
    skipReason: noteSchema.optional(),
})
    .superRefine((input, ctx) => {
    if (input.to === 'rejected' && !input.reasonCode) {
        ctx.addIssue({ code: 'custom', path: ['reasonCode'], message: 'Pick a reason before continuing.' });
    }
});
/**
 * What a bulk action acts on: the ids ticked, or "all N matching" as the filter plus the
 * rows unticked since (06-qol §3). The server resolves either to at most 200 ids.
 */
exports.pipelineSelectionSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('ids'), ids: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Select at least one.').max(exports.BULK_MAX_RECORDS, exports.BULK_TOO_MANY) }),
    zod_1.z.object({ kind: zod_1.z.literal('all-matching'), filter: exports.pipelineFilterSchema, excluded: zod_1.z.array(schemas_1.uuidSchema).max(1000).default([]) }),
]);
exports.BULK_ACTIONS = ['advance', 'reject', 'pool', 'tag', 'assign_recruiter', 'assign_assessment', 'book_exam_session', 'message'];
exports.BULK_ACTION_LABELS = {
    advance: 'Advance',
    reject: 'Reject',
    pool: 'Add to pool',
    tag: 'Tag',
    assign_recruiter: 'Assign recruiter',
    assign_assessment: 'Assign assessment',
    book_exam_session: 'Add to exam session',
    message: 'Send message',
    move: 'Move',
    export: 'Export',
};
exports.bulkActionSchema = zod_1.z.discriminatedUnion('action', [
    zod_1.z.object({ action: zod_1.z.literal('advance'), selection: exports.pipelineSelectionSchema }),
    zod_1.z.object({
        action: zod_1.z.literal('reject'),
        selection: exports.pipelineSelectionSchema,
        reasonCode: reasonCodeSchema,
        poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
        newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
        /** ADR 0046: "Also add to CV Bank" — each person with a CV, after their rejection. */
        addToCvBank: zod_1.z.boolean().default(false),
    }),
    zod_1.z
        .object({
        action: zod_1.z.literal('pool'),
        selection: exports.pipelineSelectionSchema,
        poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
        newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
    })
        .refine((input) => input.poolIds.length + input.newPoolNames.length > 0, { path: ['poolIds'], message: 'Choose at least one pool.' }),
    zod_1.z
        .object({
        action: zod_1.z.literal('tag'),
        selection: exports.pipelineSelectionSchema,
        tagIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
        newTagNames: zod_1.z.array(tagNameSchema).max(5).default([]),
    })
        .refine((input) => input.tagIds.length + input.newTagNames.length > 0, { path: ['tagIds'], message: 'Choose at least one tag.' }),
    zod_1.z.object({ action: zod_1.z.literal('assign_recruiter'), selection: exports.pipelineSelectionSchema, recruiterUserId: schemas_1.uuidSchema }),
    // S17 Q4: people in Screening move to Assessment and are sent the job's test; people
    // already there are sent it. It sends email, so like any message it cannot be undone (§L9).
    zod_1.z.object({ action: zod_1.z.literal('assign_assessment'), selection: exports.pipelineSelectionSchema }),
    // S18 Q2: the same for a test sat on paper — into Assessment, then a seat on the session,
    // with its booking email. Not undoable, for the same reason.
    zod_1.z.object({ action: zod_1.z.literal('book_exam_session'), selection: exports.pipelineSelectionSchema, examSessionId: schemas_1.uuidSchema }),
    // S26 (F15.03, moved here from S16 Q1): one email each, filled in for each person through
    // COMM-03's variables. Messages cannot be taken back, so never undoable (§L9).
    zod_1.z.object({
        action: zod_1.z.literal('message'),
        selection: exports.pipelineSelectionSchema,
        templateKey: zod_1.z.string().trim().max(120).nullable().default(null),
        subject: zod_1.z.string().trim().min(1, 'Write a subject.').max(communication_1.MESSAGE_SUBJECT_MAX),
        body: zod_1.z.string().trim().min(1, 'Write the message.').max(communication_1.MESSAGE_BODY_MAX),
    }),
]);
exports.pipelineExportSchema = zod_1.z.object({
    selection: exports.pipelineSelectionSchema,
    /** The columns on screen, in order — an export is the view, as a file. */
    columns: zod_1.z.array(zod_1.z.enum(exports.PIPELINE_COLUMNS)).min(1).max(exports.PIPELINE_COLUMNS.length),
});
/** F8.15. Screening by default; a later start is an HR Lead's skip, with a note (Q6/Q6a). */
exports.transferApplicationSchema = zod_1.z
    .object({
    targetJobId: schemas_1.uuidSchema,
    startStage: zod_1.z.enum(['screening', 'assessment', 'interview_1', 'interview_2', 'interview_3']).default('screening'),
    note: noteSchema.optional(),
    version: zod_1.z.number().int().nonnegative(),
})
    .superRefine((input, ctx) => {
    if (input.startStage !== 'screening' && !input.note) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say why they start past Screening.' });
    }
});
// --- Saved views (F8.14) ------------------------------------------------------------------
exports.PIPELINE_LAYOUTS = ['list', 'board'];
exports.savedViewStateSchema = zod_1.z.object({
    filters: exports.pipelineFilterSchema,
    hiddenColumns: zod_1.z.array(zod_1.z.enum(exports.PIPELINE_COLUMNS)).max(exports.PIPELINE_COLUMNS.length).default([]),
    layout: zod_1.z.enum(exports.PIPELINE_LAYOUTS).default('list'),
});
exports.saveViewSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Give the view a name.').max(exports.SAVED_VIEW_NAME_MAX, `Names can be up to ${exports.SAVED_VIEW_NAME_MAX} characters.`),
    isShared: zod_1.z.boolean().default(false),
    state: exports.savedViewStateSchema,
});
exports.updateViewSchema = exports.saveViewSchema.extend({ version: zod_1.z.number().int().nonnegative() });
/** The reasons that fit every selected stage (§L19: a stage's own, plus the `any` ones). */
function reasonsForStages(reasons, stages) {
    const groups = new Set(stages.map(reason_codes_1.rejectionStageFor));
    // S21 Q4: an interview judgement belongs to the round's debrief, not to a one-click reject.
    return reasons.filter((reason) => !(0, decisions_1.isDebriefReason)(reason.code) && (reason.stage === 'any' || (groups.size === 1 && groups.has(reason.stage))));
}
/**
 * §L26 "Bulk partial failure": "{n} of {total} updated. {m} failed — see details."
 * Never "successfully" (06-qol §4).
 */
function bulkResultMessage(view) {
    const label = exports.BULK_ACTION_LABELS[view.kind];
    if (view.failedCount === 0)
        return `${label}: ${view.succeededCount} of ${view.requestedCount} updated.`;
    return `${view.succeededCount} of ${view.requestedCount} updated. ${view.failedCount} failed — see details.`;
}
//# sourceMappingURL=pipeline.js.map